<script setup lang="ts">
import { useWindowScroll } from '@vueuse/core'
import type { SelectMenuItem } from '@nuxt/ui'

definePageMeta({
  middleware: 'auth'
})

// SEO
useSeoMeta({
  robots: 'noindex, nofollow'
})

// Config
const runtimeConfig = useRuntimeConfig()
const appConfig = useAppConfig()

// Idioma
const { locale, t } = useI18n()
var browserLang = navigator?.language ?? locale.value

// Router
const route = useRoute()
const router = useRouter()

// Scroll
const { y } = useWindowScroll()

const scrollTopSmooth = () => {
  window?.scrollTo({ top: 0, behavior: 'smooth' })
}

const scrollTop = () => {
  window?.scrollTo({ top: 0 })
}

// Currency Store
const currencyStore = useCurrencyStore()
const { currency } = storeToRefs(currencyStore)
if (route.query?.['curr']) {
  currency.value = String(route.query?.['curr'])
}

// Search Modals Store
const searchModalsStore = useSearchModalsStore()

watch(currency, (newCurrency) => {
  router.push({
    query: {
      ...route.query,
      'curr': newCurrency,
    }
  })
  scrollTop()
})

// Ubicacion
const selectedDestStore = useDestinationStore()
const { selectedDestination, labelDestination, destination_id, distance, latitude, longitude } = storeToRefs(selectedDestStore)

selectedDestStore.updateDestination(route)

const lat = computed(() => latitude.value ?? 0)
const long = computed(() => longitude.value ?? 0)

const getQueryString = computed(() => {  // query string
  const parameters = route.query
  return Object.keys(parameters).map(key => `${key}=${parameters[key]}`).join('&')
})

watch(selectedDestination, (newUbicacion) => { // scroll top si cambio de ubicacion
  if (newUbicacion) {
    scrollTop()
    // Si se obtiene una ubicación válida, cerrar el modal de selección (p. ej. tras elegir en el modal)
    const hasValue = newUbicacion && typeof newUbicacion === 'object' && Object.keys(newUbicacion).length > 0
    if (hasValue && import.meta.client) {
      searchModalsStore.closeDestinationModal()
    }
  }
})

const searchMap = (mapObj: { center: { lat: number, lng: number }, zoom: number }) => { // recibir emit busqueda zona
  const { 'data-destinationId': _, ...restQuery } = route.query
  router.push({
    query: {
      ...restQuery,
      'data-latitude': mapObj.center.lat,
      'data-longitude': mapObj.center.lng,
      'data-distance': distance.value,
    }
  })

  const labelZonaActual = t('labels.zonaActual')
  selectedDestination.value = {
    id: null,
    label: labelZonaActual,
    name_translated: labelZonaActual,
    location: `${mapObj.center.lat},${mapObj.center.lng}`,
  }

  scrollTop()
}

// Fecha
const selectedDiaStore = useSelectedDayStore()
const { selectedDay, date } = storeToRefs(selectedDiaStore)

watch(selectedDay, (newDia: Date | null, oldDia: Date | null) => {
  if (newDia && oldDia && formatDateApi(newDia) !== formatDateApi(oldDia)) {
    router.push({
      query: {
        ...route.query,
        'data-checkinDate': formatDateGuion(newDia),
        'GFakeDate': formatFakeDate(newDia)
      }
    })
    scrollTop()
  }
})

// Search
const nuxtApp = useNuxtApp()

const searchQuery = computed(() => {
  const baseQuery = {
    date: date.value,
    channel_id: appConfig.channel_id,
    locale: locale.value,
    currency: currency.value,
    packhour: '3,6,24'
  }

  if (destination_id.value) {
    // Si destination_id tiene valor, se usa en la query
    return {
      destination_id: destination_id.value,
      ...baseQuery
    }
  } else {
    // Si destination_id es null, undefined o 0, se añaden lat y long
    const hasValidLocation = lat.value && long.value && lat.value !== 0 && long.value !== 0
    return {
      lat: lat.value,
      long: long.value,
      ...(hasValidLocation && { distance: distance.value }),
      ...baseQuery
    }
  }
})

// PostHog funnel
const { trackSearchCompleted } = usePosthogFunnel()

// Favorites Store
const favoritesStore = useFavoritesStore()
const { invalidateSearchCache } = storeToRefs(favoritesStore)

// Estado para modales de favorito
const showLoginModal = ref(false)
const showRegisterModal = ref(false)
const hotelIdForFavorite = ref<number | null>(null)

const handleToggleFavorite = async (hotelId: number) => {
  if (loggedIn.value && !userPending.value) {
    if (!favoritesStore.favoriteHotels.has(hotelId)) {
      await favoritesStore.favoriteHotel(hotelId)
    } else {
      await favoritesStore.unfavoriteHotel(hotelId)
    }
  } else if (userPending.value) {
    const toast = useToast()
    toast.add({
      title: t('labels.user_zone.menu.favoritos'),
      description: t('labels.user_zone.favoritos.completeProfile'),
      color: 'warning',
      icon: 'i-heroicons-heart',
      actions: [{
        icon: 'i-lucide-user-circle',
        label: t('labels.user_zone.menu.perfil'),
        color: 'neutral',
        variant: 'subtle',
        onClick: () => {
          navigateTo(`/${locale.value}/user-zone/profile`)
        }
      }]
    })
  } else {
    hotelIdForFavorite.value = hotelId
    showLoginModal.value = true
  }
}

// Auth Store
const authStore = useAuthStore()
const { loggedIn, user } = useUserSession()
const userPending = computed(() => {
  return (user.value as OAuthUser)?.status?.id === 2
})


const { data: responseData, status, error: fetchError } = await useLazyFetch(`${useBaseUrl()}/search`, {
  key: `search-${getQueryString.value}`,
  server: false, // No ejecutar en el servidor
  headers: loggedIn.value ? await authStore.getAuthHeaders() : { "Content-Type": "application/json" },
  query: searchQuery,
  transform(input: any) {
    return {
      ...input,
      fetchechAt: new Date(),
      id: destination_id.value,
      currency: currency.value,
      date: date.value,
    }
  },
  getCachedData: (key, nuxtApp, ctx) => {
    // Si es un refresh manual, no usar la cache
    if (ctx.cause === 'refresh:manual') {
      return undefined
    }

    const cachedData = nuxtApp.payload.data[key] || nuxtApp.static.data[key]
    if (!cachedData) {
      return // refech data
    }

    // Mirar si ha expirado la cache
    const expirationDate = new Date(cachedData?.fetchechAt)
    expirationDate.setTime(expirationDate.getTime() + 1000 * 60 * 5) // 5 minutes
    const isExpired = new Date() > expirationDate
    if (isExpired) {
      return // refech data
    }

    // Mirar si es el mismo destino
    if (destination_id.value && cachedData?.id !== destination_id.value) {
      return // refech data
    }

    // Mirar si es la misma fecha
    if (date.value && cachedData?.date !== date.value) {
      return // refech data
    }

    // Mirar si es la misma moneda
    if (currency.value && cachedData?.currency !== currency.value) {
      return // refech data
    }

    // Mirar si lat y long han cambiado
    if (lat.value && long.value && cachedData?.lat !== lat.value && cachedData?.long !== long.value) {
      return // refech data
    }

    // Mirar si se ha invalidado la cache
    if (invalidateSearchCache.value) {
      return // refech data
    }

    return cachedData
  },
  timeout: 30000,
  onResponseError: (res) => {
    logError(res)
  }
})
const pending = computed(() => status.value === 'pending')
const searchError = computed(() => status.value === 'error')

const serverErrorMessage = computed(() => {
  if (!fetchError.value) return null
  const data: any = fetchError.value.data
  if (data?.details?.[0]?.message) {
    return data.details[0].message
  }
  return data?.msg || data?.error || t('errors.search')
})

// Variables para controlar el estado de carga y evitar hydration mismatch
const isInitialLoad = ref(true)
const showContent = ref(false)

// Computed para determinar si mostrar el skeleton
const shouldShowSkeleton = computed(() => {
  return !showContent.value || pending.value || isLoading.value || status.value === 'pending'
})

const dataHoteles: any = ref((responseData.value as any)?.data?.search?.hotels || [])
const dataSearch: any = ref((responseData.value as any)?.data?.search || {})
const dataAlternatives: any = ref((responseData.value as any)?.data?.alternatives?.hotels || [])
const dataNear: any = ref((responseData.value as any)?.data?.near?.hotels || [])

const dataAllAlternatives = computed(() => {
  const mergedArray = [...dataAlternatives.value, ...dataNear.value]
  const uniqueArray = mergedArray.reduce((acc, curr) => {
    const found = acc.find((item: any) => item.id === curr.id)
    if (!found) {
      acc.push(curr)
    }
    return acc
  }, [])
  return uniqueArray
})

watch(responseData, (newData) => { // Actualizar hoteles
  // No vaciar datos al iniciar una nueva búsqueda ("Buscar en esta zona"); solo actualizar cuando llegue la respuesta
  if (!newData) return

  const searchData = (newData as any)?.data?.search
  const hotels = searchData?.hotels ?? []

  dataHoteles.value = hotels
  dataSearch.value = searchData || {}
  dataAlternatives.value = (newData as any)?.data?.alternatives?.hotels || []
  dataNear.value = (newData as any)?.data?.near?.hotels || []
  precio_max.value = Math.ceil(dataSearch.value?.summary?.max_price / 10) * 10
  selectedPrecio.value = Math.ceil(dataSearch.value?.summary?.max_price / 10) * 10
  precio_min.value = Math.floor(dataSearch.value?.summary?.min_price / 10) * 10
  precio_min_label.value = dataSearch.value?.summary?.min_price_label
  currency_symbol.value = dataSearch.value?.summary?.currency_symbol
  estrellas.value = dataSearch.value?.search_stars?.map((star: any) => star.abbr)
  servicios.value = dataSearch.value?.search_services?.map((service: any) => service.name)

  // Resetear filtros si no hay resultados de hoteles
  if (!dataHoteles.value?.length) {
    resetFiltro()
  }
})

// Al empezar una nueva carga (p. ej. cambio de fecha), ocultar contenido y mostrar skeleton
watch(pending, (isPending) => {
  if (isPending) {
    showContent.value = false
  }
})

// Controlar cuándo mostrar el contenido para evitar hydration mismatch
watch([responseData, pending], ([newData, newPending]) => {
  if (newData && !newPending) {
    // Pequeño delay para transición suave
    setTimeout(() => {
      showContent.value = true
    }, 100)
  }
}, { immediate: true })

// Watcher adicional para el status
watch(status, (newStatus) => {
  if (newStatus === 'success' && responseData.value) {
    setTimeout(() => {
      showContent.value = true
    }, 100)
  }
})

// Funnel: search_completed cuando la búsqueda termina con destino + fecha (una vez por búsqueda)
const lastSearchFunnelKey = ref<string | null>(null)
watch([status, responseData, destination_id, date, latitude, longitude], () => {
  if (status.value !== 'success' || !responseData.value || !date.value) return
  const hasDestination = !!destination_id.value || (!!latitude.value && !!longitude.value && latitude.value !== 0 && longitude.value !== 0)
  if (!hasDestination) return
  const funnelKey = `${destination_id.value ?? 'geo'}-${date.value}-${latitude.value}-${longitude.value}`
  if (lastSearchFunnelKey.value === funnelKey) return
  lastSearchFunnelKey.value = funnelKey
  const searchData = (responseData.value as any)?.data?.search
  const hotels = searchData?.hotels ?? []
  trackSearchCompleted({
    destination_id: destination_id.value ?? undefined,
    destination_name: labelDestination.value || selectedDestination.value?.name_translated,
    date: date.value,
    currency: currency.value,
    locale: locale.value,
    results_count: hotels.length,
    has_destination: true
  })
}, { immediate: true })

// Filtros

// Horas
const selectedHora: any = ref([])

//Precio
const precio_max = ref(Math.ceil(dataSearch.value?.summary?.max_price / 10) * 10)
const selectedPrecio = ref(Math.ceil(dataSearch.value?.summary?.max_price / 10) * 10)
const precio_min = ref(Math.floor(dataSearch.value?.summary?.min_price / 10) * 10)
const precio_min_label: Ref<string> = ref(dataSearch.value?.summary?.min_price_label)
const currency_symbol: Ref<string> = ref(dataSearch.value?.summary?.currency_symbol)

// Estrellas
const estrellas = ref(dataSearch.value?.search_stars?.map((star: any) => star.abbr))
const selectedEstrellas = ref([])

// Servicios
const servicios = ref(dataSearch.value?.search_services?.map((service: any) => service.name))
const selectedServicios = ref([])

// Promoción
const promo = ref(false)

// Checkboxes horas
const check3 = ref(false)
const check6 = ref(false)
const check24 = ref(false)

// Filtrar hoteles
const showHotel = (hotel: any): boolean => {
  scrollTop()

  // Si el hotel tiene checkin_times vacio, no se muestra
  if (isCheckinTimesEmpty(hotel?.checkin_times)) {
    return false
  }

  // Si el hotel no tiene horas en filtroHoras, no se muestra
  let horasEntradaFiltro: any = {}
  for (let key in hotel?.checkin_times) {
    horasEntradaFiltro[key] = Object.keys(hotel.checkin_times[key]).filter((time: any) => hotel.checkin_times[key][time])
  }
  const filtroHoras = selectedHora.value.length ? selectedHora.value.map((hora: any) => hora.label) : []
  const arrayHorasAux = Object.values(horasEntradaFiltro).flat()
  const horasHotelDispo = arrayHorasAux.map((hora: any) => changeHourFormat(browserLang) ? format12Hour(hora) : hora)
  if (filtroHoras.length && !filtroHoras.some((hora: any) => horasHotelDispo.includes(hora))) {
    return false
  }

  // Precio
  if (primerPrecio(hotel) > selectedPrecio.value) {
    return false
  }

  // Estrellas
  if (selectedEstrellas.value?.length && !selectedEstrellas.value.some((str: any) => str === hotel?.stars?.abbr)) {
    return false
  }

  // Servicios
  let arrayServicios = Object.values(hotel?.services ?? {}).map((value: any) => value.name)
  if (selectedServicios.value?.length && !selectedServicios.value.every((value: any) => arrayServicios.includes(value))) {
    return false
  }

  // Filtro Promo
  if (promo.value && !hotel?.promo.length) {
    return false
  }

  // Filtro packs
  const filtroChecks: any = { 'check3': check3.value, 'check6': check6.value, 'check24': check24.value }
  if (filtroChecks.check3 || filtroChecks.check6 || filtroChecks.check24) {
    const packNumbers = [3, 6, 24]

    if (filtroChecks) {
      let packMatch = false;

      for (let i = 0; i < packNumbers.length; i++) {
        const packNumber = packNumbers[i]
        if (packNumber === undefined) continue

        const packSelected = filtroChecks[`check${packNumber}`]
        const hotelPack = hotel?.pack_min_price?.[packNumber]?.price ?? 0

        if (packSelected && hotelPack) {
          packMatch = true
          break
        }
      }

      if (!packMatch) {
        return false
      }
    }
  }

  // Si todas las condiciones de filtro se cumplen, se muestra el hotel
  return true
}

const filteredHoteles: any = computed(() => {
  let filterData = [...dataHoteles.value ?? []]
  return filterData.filter(showHotel)
})

const filteredMap: any = computed(() => {
  let filterData = [...dataHoteles.value ?? []]
  return filterData.map(item => showHotel(item) ? item : { lat: item.latitude, lng: item.longitude })
})
const mapHotels = computed(() => {
  return [...filteredMap.value, ...dataAlternatives.value, ...dataNear.value]
})

// Horas disponibles
const updateHorasEntrada = () => {
  let horasCount: any = {}
  for (let i = 0; i < filteredHoteles.value?.length; i++) {
    const chekinTimes = filteredHoteles.value?.[i]?.checkin_times
    let arrayHoras: any = Object.values(chekinTimes ?? {}).reduce((acc: any, curr: any) => {
      let keys = Object.keys(curr).filter(key => curr[(key)])
      return acc.concat(keys)
    }, [])
    arrayHoras = [...new Set(arrayHoras)]

    arrayHoras.forEach((hora: any) => {
      if (horasCount[hora]) {
        horasCount[hora]++;
      } else {
        horasCount[hora] = 1;
      }
    })
  }

  let contadorHoras = Object.keys(horasCount).map(hora => ({ hora, numHoteles: horasCount[hora] }))
  const horasEntrada = Array.from({ length: 24 }, (_, index) => {
    const hour = index
    const stringHour = `${hour.toString().padStart(2, '0')}:00`
    let hotel: any = contadorHoras.find(hotel => hotel.hora === stringHour)
    if (hotel?.numHoteles !== undefined) {
      return {
        label: changeHourFormat(browserLang) ? format12Hour(stringHour) : stringHour,
        numHoteles: hotel.numHoteles,
        chip: {
          color: hotel.numHoteles ? 'success' : 'error',
        }
      }
    }
  }).filter(Boolean)
  return horasEntrada
}
const horasEntrada = ref(updateHorasEntrada())

watch([check3, check6, check24, selectedPrecio, selectedEstrellas, selectedServicios, promo], () => {
  horasEntrada.value = updateHorasEntrada()
})

// Horas entrada con label de título
const horasEntradaWithLabel = computed(() => {
  if (!horasEntrada.value?.length) return []
  return [
    { type: 'label' as const, label: t('strapi.searchPage.filtroHora') },
    ...horasEntrada.value.filter((item): item is NonNullable<typeof item> => item !== undefined).map(item => ({
      label: item.label,
      numHoteles: item.numHoteles,
      chip: {
        color: (item.chip.color === 'success' ? 'success' : 'error') as 'success' | 'error'
      }
    }))
  ] as SelectMenuItem[]
})

// Reset filtro
const resetFiltro = () => {
  selectedHora.value = []
  selectedPrecio.value = precio_max.value
  selectedEstrellas.value = []
  selectedServicios.value = []
  promo.value = false
  check3.value = false
  check6.value = false
  check24.value = false

  isOpen.value = false
}

watch(selectedDestination, (newUbicacion) => { // reset filtro si cambio de ubicacion
  if (newUbicacion) {
    resetFiltro()
  }
})

// Check filtro
const hasFilters = computed(() => {
  return (
    (selectedPrecio.value && precio_max.value && selectedPrecio.value !== precio_max.value) ||
    selectedEstrellas.value.length > 0 ||
    selectedServicios.value.length > 0 ||
    selectedHora.value.length > 0 ||
    check3.value ||
    check6.value ||
    check24.value
  )
})

// Ordenación

// Ordenación de search
const ordenSearch: any = [
  t('strapi.searchPage.ordenacion.0'),
  t('strapi.searchPage.ordenacion.1'),
  t('strapi.searchPage.ordenacion.2'),
  t('strapi.searchPage.ordenacion.3'),
  t('strapi.searchPage.ordenacion.4'),
]
const selectedOrden: any = ref(ordenSearch[0] ?? t('strapi.searchPage.ordenacion.0'))

const sortData = (data: any) => {
  let sortedData = [...data]
  const sortFunction = ordenarSearch[ordenSearch.indexOf(selectedOrden.value)]
  if (sortFunction) {
    sortedData.sort(sortFunction)
  }
  return sortedData
}

const sortedHoteles: any = computed(() => {
  return sortData(filteredHoteles.value)
})

const sortAllAlternatives: any = computed(() => {
  return sortData(dataAllAlternatives.value)
})

// Slideover
const isOpen = ref(false)

// Breadcrumb
let links = computed(() => [
  {
    label: 'Home',
    to: `/${locale.value}`
  },
  {
    label: labelDestination.value
  }
])

// Data layers
const { proxy } = useScriptGoogleTagManager()

const search_country = route.query?.['data-country']
const checkin_date = route.query?.['data-checkinDate']
const user_language = arrayLangKeys[locale.value]
const fecha = new Date()
const year = fecha.getFullYear()
const month = ("0" + (fecha.getMonth() + 1)).slice(-2) // Los meses en JavaScript comienzan desde 0
const day = ("0" + fecha.getDate()).slice(-2)
const date_now = `${year}-${month}-${day}`
const time_now = `${fecha.getHours()}:${fecha.getMinutes()}:${fecha.getSeconds()}`
const phCookie: any = useCookie(`ph_${runtimeConfig.public.POSTHOG_KEY}_posthog`)
const session_id = phCookie?.value?.distinct_id
const hotels = computed(() => {
  return filteredHoteles.value.map((hotel: any) => {
    return {
      chain: hotel?.chain,
      currency: currency.value,
      hotel_id: hotel?.id,
      name: hotel?.name,
      price: primerPrecio(hotel),
      star: hotel?.star?.abbr
    }
  })
})

// packHours para GTM view_item_list (query o valor por defecto de búsqueda)
const packHours = computed(() => route.query?.['data-packHours'] ?? '3,6,24')
// destination_country_iso para Lookup Table - DL - Destination Country - ISO (item_category)
const destination_country_iso = computed(() => selectedDestination.value?.country_id ?? null)

function pushSearchDataLayer() {
  proxy.dataLayer.push({
    brainsins: {
      city: `city-${destination_id.value}`,
      pageType: "category",
    },
    checkin_date,
    city_url: `${runtimeConfig.public.BASE_URL}${selectedDestination.value?.url}`,
    date_now,
    destination_id: destination_id.value,
    destination_country_iso: destination_country_iso.value,
    hotel_avg_price: dataSearch.value?.summary?.avg_price,
    hotel_city: labelDestination.value,
    hotel_min_price: dataSearch.value?.summary?.min_price,
    hotels: hotels.value,
    number_of_purchases: 0,
    packHours: packHours.value,
    results: filteredHoteles.value.length,
    rfmc: 0,
    search: labelDestination.value,
    search_country,
    search_destination_id: destination_id.value,
    session_id,
    time_now,
    user_currency: currency.value,
    user_language
  })
}

// Actualizar dataLayer cuando cambien resultados, destino o datos de búsqueda
watch(
  [filteredHoteles, dataSearch, () => destination_id.value, selectedDestination, currency],
  () => { pushSearchDataLayer() },
  { immediate: true }
)

// SEO
const canonicalUrl = computed(() => {
  const baseUrl = runtimeConfig.public.BASE_URL || ''
  return `${baseUrl}/${locale.value}/search`
})

useSeoMeta({
  title: t('meta.search.title', { count: filteredHoteles.value.length, city_name: labelDestination.value }),
  description: t('meta.search.description', { city_name: labelDestination.value }),
  keywords: t('meta.search.keywords', { city_name: labelDestination.value }),
  ogTitle: t('meta.search.title', { count: filteredHoteles.value.length, city_name: labelDestination.value }),
  ogDescription: t('meta.search.description', { city_name: labelDestination.value }),
  ogUrl: canonicalUrl
})

useHead({
  link: [
    {
      rel: 'canonical',
      href: canonicalUrl
    }
  ]
})

// Hover hotel
const hoverHotelID = ref(0)

// Es mobile
const deviceStore = useDeviceStore()
const isMobile = deviceStore.isMobile

// Scroll load
const itemsPerPage = 10
const currentPage = ref(1)
const visibleItems = ref<any[]>(sortedHoteles.value.slice(0, itemsPerPage))
const loading = ref(false)

watch(sortedHoteles, (hoteles) => {
  visibleItems.value = hoteles.slice(0, itemsPerPage)
  currentPage.value = 1
})

const loadMoreItems = () => {
  if (visibleItems.value.length === sortedHoteles.value.length) return
  if (loading.value) return
  loading.value = true
  setTimeout(() => {
    const start = currentPage.value * itemsPerPage
    const end = start + itemsPerPage
    visibleItems.value.push(...sortedHoteles.value.slice(start, end))
    currentPage.value += 1
    loading.value = false
  }, 100) // Simula una carga de datos
}

const onScroll = () => {
  const { scrollTop, clientHeight, scrollHeight } = document.documentElement
  if (scrollTop + clientHeight >= scrollHeight - 1500) { // 500px antes de llegar al final
    loadMoreItems()
  }
}

onMounted(() => {
  isInitialLoad.value = false
  window.addEventListener('scroll', onScroll)

  // Si no hay ubicación seleccionada, abrir el modal de selección (mismo que en SearchInput)
  nextTick(() => {
    const dest = selectedDestination.value
    const hasNoDestination = !dest || (typeof dest === 'object' && Object.keys(dest).length === 0)
    if (hasNoDestination) {
      searchModalsStore.openDestinationModal()
    }
  })

  // Verificar si ya hay datos al montar el componente
  if (responseData.value && !pending.value) {
    setTimeout(() => {
      showContent.value = true
    }, 100)
  }
})

onUnmounted(() => {
  window.removeEventListener('scroll', onScroll)
})

// App round
const app_round = appConfig.ui.button.slots.base

// Loading
const { isLoading } = useLoadingIndicator()

// Manejar navegación a otra ruta - llamar scrollTop antes de salir
onBeforeRouteLeave((to, from) => {
  scrollTop()
})

const handleErrorReload = () => {
  scrollTop()
  if (import.meta.client) {
    window.location.reload()
  }
}

</script>

<template>

  <!-- Botón mapa -->
  <div v-if="y >= 500 && isMobile" class="fixed top-16 z-50 mt-2">
    <UButton icon="i-lucide-arrow-up" color="neutral" size="xs" @click="scrollTopSmooth">
      <UIcon name="i-lucide-map" class="w-5 h-5" />
    </UButton>
  </div>

  <!-- Filtros -->
  <div
    class="justify-between z-10 sm:z-20 sticky top-16 backdrop-blur-lg bg-white/80 flex flex-col xl:flex-row p-5 py-3 xl:py-5 w-full sm:gap-3 max-w-screen-2xl items-start">

    <div class="w-full xl:w-fit flex items-center justify-between">

      <!-- BreadCrumb -->
      <UBreadcrumb :items="links" />

      <div class="flex">

        <!-- Filtros plegado -->
        <div class="xl:hidden flex gap-2 items-center">

          <USlideover :title="$t('buttons.filtros')" v-model:open="isOpen">

            <!-- Botón filtros -->
            <UChip inset :show="hasFilters">
              <UButton :aria-label="$t('buttons.filtros')" icon="i-lucide-sliders-horizontal" variant="subtle"
                color="neutral" @click="isOpen = true" />
            </UChip>

            <template #body>
              <div class="flex flex-col gap-4">

                <!-- Hora entrada -->
                <div v-if="dataHoteles?.length">
                  <h3>{{ $t('strapi.searchPage.filtroHora') }}</h3>
                  <div class="py-2">
                    <USelectMenu :disabled="!horasEntrada?.length" size="md" class="w-full" v-model="selectedHora"
                      :items="horasEntradaWithLabel" multiple :search-input="false"
                      :placeholder="$t('strapi.searchPage.filtroHora')" :ui="{
                        item: 'gap-1 p-1 py-2'
                      }">
                      <template #leading="{ ui }">
                        <UChip v-if="selectedHora.length" color="success" inset standalone
                          :class="ui.itemLeadingChip()" />
                        <UIcon v-else name="i-lucide-clock" class="text-neutral-400 w-4 h-4" />
                      </template>
                      <template #item-label="{ item }">
                        <div v-if="item" class="flex items-center gap-1.5">
                          <span>{{ (item as any).label }}</span>
                          <span class="text-muted text-xs">
                            {{ (item as any).numHoteles }} {{ $t('labels.hoteles') }}
                          </span>
                        </div>
                      </template>
                      <template #empty>
                        {{ t('buttons.noDispo') }}
                      </template>
                    </USelectMenu>
                  </div>
                </div>

                <!-- Packs -->
                <div v-if="dataHoteles?.length">
                  <h3>{{ $t('labels.pack') }}</h3>
                  <div class="py-2 flex gap-4 w-full">
                    <UCheckbox id="pack3-2" v-model="check3" label="3h" />
                    <UCheckbox id="pack6-2" v-model="check6" label="6h" />
                    <UCheckbox id="pack24-2" v-model="check24" label="24h" />
                  </div>
                </div>

                <!-- Precio -->
                <div v-if="selectedPrecio">
                  <h3>{{ $t('strapi.searchPage.filtroPrecio') }}</h3>
                  <div class="py-4">
                    <div class="flex flex-col gap-4 w-full">
                      <div class="flex gap-2 items-center">
                        <span id="precioMin-2" class="text-nowrap">{{ isRightSymbol(precio_min_label, currency_symbol) ?
                          `${precio_min} ${currency_symbol}` : `${currency_symbol} ${precio_min}` }}</span>
                        <USlider color="neutral" :step="10" size="sm" :placeholder="$t('buttons.buscar')"
                          v-model="selectedPrecio" :max="precio_max" :min="precio_min" />
                        <span id="precioMax-2" class="text-nowrap">{{ isRightSymbol(precio_min_label, currency_symbol) ?
                          `${selectedPrecio} ${currency_symbol}` : `${currency_symbol} ${selectedPrecio}` }}</span>
                      </div>
                    </div>
                  </div>
                </div>

                <!-- Estrellas -->
                <div>
                  <h3>{{ $t('strapi.searchPage.filtroEstrella') }}</h3>
                  <div class="py-2">
                    <USelectMenu id="category-2" :disabled="!estrellas?.length" size="md" class="w-full"
                      v-model="selectedEstrellas" :items="estrellas" multiple
                      :placeholder="$t('strapi.searchPage.filtroEstrella')" :search-input="false">
                      <template #item="{ item: option }">
                        <span v-if="option === '-'">{{ option }}</span>
                        <template v-else>
                          <UIcon v-for="(index) in splitEstrellas(option)?.countEstrellas" :key="index" class="text-xs"
                            name="i-heroicons-star-solid" />
                          {{ splitEstrellas(option)?.rightString }}
                        </template>
                      </template>
                    </USelectMenu>
                  </div>
                </div>

                <!-- Servicios -->
                <div class="col-span-2">
                  <h3>{{ $t('strapi.searchPage.filtroServicio') }}</h3>
                  <div class="py-2">
                    <USelectMenu id="servicios-2" :disabled="!servicios?.length" size="md" class="w-full"
                      v-model="selectedServicios" :items="servicios" multiple
                      :placeholder="$t('strapi.searchPage.filtroServicio')" :search-input="{
                        placeholder: `${t('buttons.buscar')}...`,
                      }">
                      <template #empty>
                        {{ t('buttons.noDispo') }}
                      </template>
                    </USelectMenu>
                  </div>
                </div>

                <USeparator class="my-4" />

                <div class="flex justify-between gap-2">
                  <UButton :aria-label="$t('buttons.borrar')" color="neutral" size="lg" variant="link"
                    icon="i-lucide-rotate-cw" @click="resetFiltro">{{ $t('buttons.reiniciar') }}</UButton>
                  <div>
                    <UButton :aria-label="$t('buttons.cerrar')" size="lg" color="neutral" variant="link"
                      @click="isOpen = false">{{
                        $t('buttons.cerrar') }}</UButton>
                    <UButton :aria-label="$t('buttons.aplicar')" size="lg" color="neutral" @click="isOpen = false">{{
                      $t('buttons.aplicar') }}</UButton>
                  </div>
                </div>

              </div>
            </template>

          </USlideover>

          <!-- Botón reset -->
          <UButton color="neutral" class="xl:hidden" size="md" variant="link" icon="i-lucide-rotate-cw"
            @click="resetFiltro" :aria-label="$t('buttons.reiniciar')" />

        </div>
      </div>
    </div>

    <!-- Filtros desplegado -->
    <div class="w-full xl:w-fit flex flex-wrap items-center justify-between sm:justify-end sm:gap-3">

      <!-- Hora entrada -->
      <USelectMenu v-if="dataHoteles?.length" :disabled="!horasEntrada?.length" size="lg"
        class="hidden xl:flex min-w-40 max-w-40 ps-9 pe-9" v-model="selectedHora" :items="horasEntradaWithLabel"
        multiple :search-input="false" :placeholder="$t('strapi.searchPage.filtroHora')" :ui="{
          item: 'gap-1 p-1 py-2'
        }">
        <template #leading="{ ui }">
          <UChip v-if="selectedHora.length" color="success" inset standalone :class="ui.itemLeadingChip()" />
          <UIcon v-else name="i-lucide-clock" class="text-neutral-400 w-4 h-4" />
        </template>

        <template #item-label="{ item }">
          <div v-if="item" class="flex items-center gap-1.5">
            <span>{{ (item as any).label }}</span>
            <span class="text-muted text-xs">
              {{ (item as any).numHoteles }} {{ $t('labels.hoteles') }}
            </span>
          </div>
        </template>

        <template #empty>
          {{ t('buttons.noDispo') }}
        </template>

      </USelectMenu>

      <!-- Packs -->
      <div v-if="dataHoteles?.length"
        :class="`hidden xl:flex gap-2 sm:gap-5 bg-white ring ring-inset ring-accented py-2 ${app_round} pl-2.5 pr-3 justify-between w-fit`">
        <UCheckbox id="pack3" v-model="check3" label="3h" />
        <UCheckbox id="pack6" v-model="check6" label="6h" />
        <UCheckbox id="pack24" v-model="check24" label="24h" />
      </div>

      <div class="hidden xl:flex flex-wrap gap-3 items-center">

        <!-- Precio -->
        <UPopover :aria-label="$t('strapi.searchPage.filtroPrecio')">
          <UButton id="price" :aria-label="$t('strapi.searchPage.filtroPrecio')" :disabled="!selectedPrecio" size="md"
            color="neutral" variant="outline" :label="$t('strapi.searchPage.filtroPrecio')"
            trailing-icon="i-lucide-chevron-down" class="w-40 justify-between" :ui="{
              base: 'py-2 px-3 hover:bg-white',
            }" />
          <template #content>
            <div class="p-4">
              <div class="flex flex-col gap-4 w-72">
                <div class="flex gap-2 items-center">
                  <span id="precioMin" class="text-nowrap">{{ isRightSymbol(precio_min_label, currency_symbol) ?
                    `${precio_min} ${currency_symbol}` : `${currency_symbol} ${precio_min}` }}</span>
                  <USlider color="neutral" :step="10" size="xs" v-model="selectedPrecio" :max="precio_max"
                    :min="precio_min" />
                  <span id="precioMax" class="text-nowrap">{{ isRightSymbol(precio_min_label, currency_symbol) ?
                    `${selectedPrecio} ${currency_symbol}` : `${currency_symbol} ${selectedPrecio}` }}</span>
                </div>
              </div>
            </div>
          </template>
        </UPopover>

        <!-- Estrellas -->
        <USelectMenu id="category" :disabled="!estrellas?.length" size="lg" class="w-40" v-model="selectedEstrellas"
          :items="estrellas" multiple :placeholder="$t('strapi.searchPage.filtroEstrella')" :search-input="false">
          <template #item-label="{ item: option }">
            <span v-if="option === '-'">{{ option }}</span>
            <template v-else>
              <UIcon v-for="(index) in splitEstrellas(option)?.countEstrellas" :key="index" class="text-xs"
                name="i-heroicons-star-solid" />
              {{ splitEstrellas(option)?.rightString }}
            </template>
          </template>
        </USelectMenu>

        <!-- Servicios -->
        <USelectMenu id="servicios" :disabled="!servicios?.length" size="lg" class="w-40" v-model="selectedServicios"
          :items="servicios" multiple :placeholder="$t('strapi.searchPage.filtroServicio')" :search-input="{
            placeholder: `${t('buttons.buscar')}...`,
          }" :ui="{
            content: 'w-56',
          }">
          <template #empty>
            {{ t('buttons.noDispo') }}
          </template>
        </USelectMenu>

      </div>

      <!-- Reset filtros -->
      <UButton color="neutral" class="hidden xl:flex" size="sm" variant="link" icon="i-lucide-rotate-cw"
        @click="resetFiltro" :aria-label="$t('buttons.reiniciar')" :label="$t('buttons.reiniciar')">
      </UButton>
    </div>
  </div>

  <div class="grid grid-cols-1 xl:grid-cols-2 max-w-screen-2xl w-full">
    <div class="z-10 bg-white xl:bg-transparent border border-neutral-200 xl:border-none rounded-t-3xl">
      <div class="h-full p-5">

        <!-- Bar -->
        <div class="flex justify-center pb-4 xl:hidden select-none" style="touch-action: none;">
          <span class="block h-1 w-[100px] bg-neutral-200 rounded-full"></span>
        </div>

        <!-- Usar ClientOnly para evitar hydration mismatch -->
        <ClientOnly>
          <!-- Mostrar skeleton o error inicialmente -->
          <div v-if="shouldShowSkeleton" class="h-full flex flex-col gap-y-7">
            <div v-if="searchError">
              <UEmpty icon="i-lucide-alert-circle" :description="serverErrorMessage ?? t('errors.search')"
                variant="naked" size="xl" :actions="[{
                  label: $t('buttons.reiniciar'),
                  icon: 'i-lucide-rotate-cw',
                  color: 'primary',
                  variant: 'solid',
                  onClick: handleErrorReload,
                  size: 'lg'
                }]" />
            </div>
            <template v-else>
              <div class="w-full flex justify-between mb-4">
                <USkeleton class="w-5/12 h-8 rounded-full animate-pulse" />
                <USkeleton class="w-2/12 h-8 rounded-full animate-pulse" />
              </div>
              <div class="w-full flex flex-col gap-10 sm:gap-8">
                <div v-if="!isMobile" class="flex gap-3 flex-col sm:flex-row" v-for="i in 6">
                  <USkeleton class="w-full sm:w-5/12 h-44 rounded-2xl animate-pulse" />
                  <USkeleton class="w-full sm:w-7/12 h-44 rounded-2xl animate-pulse" />
                </div>
                <HotelSkeleton v-else v-for="i in 6" class="animate-pulse" />
              </div>
            </template>
          </div>

          <!-- Contenido real con transición -->
          <Transition name="fade" mode="out-in">
            <div v-if="showContent" class="h-full flex flex-col gap-y-7">
              <div class="space-y-10">
                <!-- Sin Hoteles -->
                <UEmpty
                  v-if="selectedDestination && selectedDestination?.category_id === 2 && !selectedDestination?.order_ratio && !dataHoteles?.length"
                  :description="$t('strapi.searchPage.sinHoteles.titulo')" icon="i-lucide-building-2" variant="naked"
                  size="xl" :actions="[{
                    label: $t('labels.buscarOtraUbicacion'),
                    color: 'primary',
                    icon: 'i-lucide-map',
                    size: 'sm',
                    onClick: () => {
                      searchModalsStore.openDestinationModal()
                    }
                  }]" />

                <!-- Sin resultados -->
                <UEmpty v-else-if="!dataHoteles?.length" :description="$t('strapi.searchPage.sinResultados.titulo')"
                  variant="naked" size="xl" :actions="[{
                    label: $t('labels.buscarOtraUbicacion'),
                    color: 'primary',
                    icon: 'i-lucide-map',
                    size: 'sm',
                    onClick: () => {
                      searchModalsStore.openDestinationModal()
                    }
                  }, {
                    label: $t('buttons.buscarDia'),
                    color: 'neutral',
                    variant: 'subtle',
                    icon: 'i-lucide-calendar',
                    size: 'sm',
                    onClick: () => {
                      searchModalsStore.openCalendarModal()
                    }
                  }]" />

                <!-- Sin hoteles filtro -->
                <UEmpty v-else-if="!filteredHoteles.length"
                  :description="$t('strapi.searchPage.sinResultadoFiltro.titulo')" icon="i-lucide-filter"
                  variant="naked" size="xl" :actions="[{
                    label: $t('buttons.reiniciar'),
                    icon: 'i-lucide-rotate-cw',
                    size: 'lg',
                    color: 'neutral',
                    variant: 'solid',
                    onClick: resetFiltro
                  }]" />

                <!-- Resultados hoteles -->
                <div v-else>
                  <div v-if="(responseData as any)?.data?.search?.search_message" class="pb-5">
                    <UAlert icon="i-lucide-info" color="secondary" variant="subtle"
                      :title="(responseData as any)?.data?.search?.search_message" />
                  </div>
                  <div class="lg:flex justify-between items-center">
                    <p class="text-lg sm:text-lg lg:text-xl py-1.5 px-2.5 lg:p-0 font-bold xl:font-normal">
                      {{ filteredHoteles.length }} {{ $t('strapi.searchPage.resultados') }} {{ labelDestination }}
                    </p>
                    <!-- Ordenación -->
                    <div>
                      <USelectMenu v-model="selectedOrden" :items="ordenSearch" class="w-fit text-neutral-500"
                        variant="none" :ui="{ content: 'w-fit' }" :search-input="false" />
                    </div>
                  </div>

                  <div class="grid grid-cols-1 gap-10 sm:gap-8 mt-4">
                    <HotelSearchCard :data="hotel" :array-horas="getHorasHotel(hotel, browserLang)"
                      v-for="(hotel) in visibleItems" :key="`${destination_id}-${hotel.id}`"
                      @hover-hotel="(id: number) => hoverHotelID = id" :urlParams="getQueryString" :alternative="false"
                      @toggle-favorite="handleToggleFavorite" />
                    <div v-if="loading">
                      <div class="w-full flex flex-col gap-10 sm:gap-8">
                        <div v-if="!isMobile" class="flex gap-3 flex-col sm:flex-row" v-for="i in itemsPerPage">
                          <USkeleton class="w-full sm:w-5/12 h-44 rounded-2xl" />
                          <USkeleton class="w-full sm:w-7/12 h-44 rounded-2xl" />
                        </div>
                        <HotelSkeleton v-else v-for="i in itemsPerPage" />
                      </div>
                    </div>
                  </div>
                </div>

                <!-- On demand -->
                <UAlert color="primary" variant="subtle" icon="i-lucide-search">
                  <template #title>
                    <span><b>{{ $t('strapi.searchPage.onDemand.titulo') }}</b></span>
                    <UButton class="px-0 md:px-2" to="https://microstays.byhours.com/ondemand-es-es" target="_blank"
                      color="primary" variant="link" :aria-label="$t('strapi.searchPage.onDemand.subtitulo')">{{
                        $t('strapi.searchPage.onDemand.subtitulo') }}</UButton>
                  </template>
                </UAlert>
              </div>

              <!-- Resultados alternatives -->
              <div v-if="sortAllAlternatives.length">
                <div class="flex justify-between items-center">
                  <p class="text-base sm:text-lg lg:text-xl">
                    {{ $t('strapi.searchPage.resultadosAlternativos') }} {{ labelDestination }}
                  </p>
                </div>
                <div ref="cardContainer" class="grid grid-cols-1 gap-10 sm:gap-8 mt-4">
                  <HotelSearchCard :data="hotel" :array-horas="getHorasHotel(hotel, browserLang)"
                    v-for="(hotel) in sortAllAlternatives" :key="`alt-${destination_id}-${hotel.id}`"
                    @hover-hotel="(id: number) => hoverHotelID = id" :urlParams="getQueryString" :alternative="false"
                    @toggle-favorite="handleToggleFavorite" />
                </div>
              </div>
            </div>
          </Transition>

          <!-- Fallback para el servidor -->
          <template #fallback>
            <div class="h-full flex flex-col gap-y-7">
              <div class="w-full flex justify-between mb-4">
                <USkeleton class="w-5/12 h-8 rounded-full animate-pulse" />
                <USkeleton class="w-2/12 h-8 rounded-full animate-pulse" />
              </div>
              <div class="w-full flex flex-col gap-10 sm:gap-8">
                <div v-if="!isMobile" class="flex gap-3 flex-col sm:flex-row" v-for="i in 6">
                  <USkeleton class="w-full sm:w-5/12 h-44 rounded-2xl animate-pulse" />
                  <USkeleton class="w-full sm:w-7/12 h-44 rounded-2xl animate-pulse" />
                </div>
                <HotelSkeleton v-else v-for="i in 6" class="animate-pulse" />
              </div>
            </div>
          </template>
        </ClientOnly>
      </div>
    </div>

    <!-- Mapa -->
    <div class="z-0 h-[65vh] xl:h-[calc(100vh-9rem)] order-first xl:order-last sticky top-30 sm:top-48 xl:top-36">
      <div class="h-full px-5 pb-5">
        <div class="h-full bg-neutral-50 flex justify-center items-center rounded-3xl relative">
          <ClientOnly>
            <HotelMap :pending="pending" @search-map="searchMap" :boundHoteles="filteredHoteles"
              :url-params="getQueryString" :lat="Number(lat)" :lon="Number(long)" :hover-hotel="hoverHotelID"
              :all-hotels="mapHotels" @toggle-favorite="handleToggleFavorite" />
            <template #fallback>
              <USkeleton class="w-full h-full rounded-3xl" />
            </template>
          </ClientOnly>
        </div>
      </div>
    </div>


    <!-- Modales de autenticación (Mapa) -->
    <AuthModals v-model:show-login-modal="showLoginModal" v-model:show-register-modal="showRegisterModal"
      :hotel-id="hotelIdForFavorite ?? 0" @login-success="() => {
        if (hotelIdForFavorite) favoritesStore.favoriteHotel(hotelIdForFavorite)
        invalidateSearchCache = true
        reloadNuxtApp()
      }" @register-success="() => {
        if (hotelIdForFavorite) favoritesStore.favoriteHotel(hotelIdForFavorite)
      }" />
  </div>
</template>

<style scoped>
.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.15s ease;
}

.fade-enter-from,
.fade-leave-to {
  opacity: 0;
}

.animate-pulse {
  animation: pulse 2s cubic-bezier(0.4, 0, 0.6, 1) infinite;
}

@keyframes pulse {

  0%,
  100% {
    opacity: 1;
  }

  50% {
    opacity: .5;
  }
}

/* Transición suave cuando aparece el contenido real */
.h-full>div:not([v-if]) {
  transition: opacity 0.15s ease-in-out;
}
</style>